export type GoogleSSOCallbackResult = "success" | "error" | null;

const AUTH_TOKEN_PARAM = "authToken";
const REFRESH_TOKEN_PARAM = "refreshToken";
const ERROR_PARAM = "error";

let callbackResult: GoogleSSOCallbackResult = null;

/**
 * Consumes the SSO tokens that the backend appended to the current URL
 * (e.g. /app?authToken=...&refreshToken=...) after a Google login roundtrip.
 * Stores the tokens the same way as the email-login flow and strips them
 * from the URL so they do not linger in history or browser/server logs.
 * Call this once before the app renders.
 */
export function handleGoogleSSOCallback(): GoogleSSOCallbackResult {
  const params = new URLSearchParams(window.location.search);

  const authToken = params.get(AUTH_TOKEN_PARAM);
  const refreshToken = params.get(REFRESH_TOKEN_PARAM);
  const hasError = params.has(ERROR_PARAM);

  if (!authToken && !refreshToken && !hasError) {
    return null;
  }

  if (authToken && refreshToken) {
    localStorage.setItem("token", authToken);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("authCodeAccepted", "true");
  }

  callbackResult =
    hasError || !authToken || !refreshToken
      ? "error"
      : "success";

  window.history.replaceState(
    {},
    document.title,
    window.location.pathname + window.location.hash
  );
  return callbackResult;
}

/** Returns and clears the result recorded by handleGoogleSSOCallback(). */
export function consumeGoogleSSOCallbackResult(): GoogleSSOCallbackResult {
  const result = callbackResult;
  callbackResult = null;
  return result;
}

/** Backend endpoint that starts the redirect-based Google login flow. */
export function getGoogleLoginUrl(): string {
  const apiUrl = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");
  return `${apiUrl}/auth/google/login`;
}
